import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { TimeInterval } from '../../types/types';
import ModernPeriodSelector from './ModernPeriodSelector';
import AdminPageHeader from '../common/AdminPageHeader';
import PlatformKpiGrid, { PlatformOverview } from './campaign/PlatformKpiGrid';
import ActivityPeriodRow, { ActivityData } from './campaign/ActivityPeriodRow';
import GmvRevenueChart from './campaign/GmvRevenueChart';
import RoasTrendChart from './campaign/RoasTrendChart';
import CampaignStatusDonut from './campaign/CampaignStatusDonut';
import SubmissionFunnelChart from './campaign/SubmissionFunnelChart';
import BudgetOverviewCard from './campaign/BudgetOverviewCard';
import TopCampaignsTable from './campaign/TopCampaignsTable';
import TopBrandsTable from './campaign/TopBrandsTable';
import TopCreatorsTable from './campaign/TopCreatorsTable';
import {
  fetchPlatformOverview,
  fetchActivityByPeriod,
  fetchRevenueTrend,
  fetchRoasTrend,
  fetchCampaignsOverview,
  fetchSubmissionsStats,
  fetchBudgetOverview,
  fetchTopCampaignsByGmv,
  fetchTopBrandsByGmv,
  fetchTopCreatorsByGmv,
} from '../../services/campaignAnalyticsService';
import { getUsers, getFlaggedSubmissions } from '../../services/adminService';
import { ROUTES } from '../../routes/constants';
import { useForceRefresh } from '../../hooks/useForceRefresh';
import RefreshButton from '../common/RefreshButton';

const SectionLabel: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => (
  <p className="text-[11px] font-semibold text-[#9B919D] uppercase tracking-[0.12em] mb-3">
    {children}
  </p>
);

const AdminAnalyticsPage: React.FC = () => {
  const navigate = useNavigate();
  const [filter, setFilter] = useState<TimeInterval>('month');
  const [overview, setOverview] = useState<PlatformOverview | null>(null);
  const [overviewLoading, setOverviewLoading] = useState(true);
  const [activity, setActivity] = useState<ActivityData | null>(null);
  const [activityLoading, setActivityLoading] = useState(true);
  const [pendingCreatorsCount, setPendingCreatorsCount] = useState(0);
  const [flaggedCount, setFlaggedCount] = useState(0);
  const [refreshToken, setRefreshToken] = useState(0);

  useEffect(() => {
    getUsers({ status: 'pending', page: 1, limit: 1, eligible: true })
      .then((r) => setPendingCreatorsCount(r?.pagination?.total ?? 0))
      .catch(() => setPendingCreatorsCount(0));
    getFlaggedSubmissions({ page: 1, limit: 1 })
      .then((r) => setFlaggedCount(r?.pagination?.total ?? 0))
      .catch(() => setFlaggedCount(0));
  }, []);

  const loadOwnData = useCallback(async () => {
    setOverviewLoading(true);
    try {
      const d = await fetchPlatformOverview();
      setOverview(d);
    } catch {
      setOverview(null);
    } finally {
      setOverviewLoading(false);
    }
  }, []);

  useEffect(() => {
    loadOwnData();
  }, [loadOwnData]);

  useEffect(() => {
    setActivityLoading(true);
    fetchActivityByPeriod(filter)
      .then((d: ActivityData) => setActivity(d))
      .catch(() => setActivity(null))
      .finally(() => setActivityLoading(false));
  }, [filter, refreshToken]);

  const loadData = useCallback(async () => {
    await Promise.allSettled([
      loadOwnData(),
      fetchActivityByPeriod(filter),
      fetchRevenueTrend(),
      fetchRoasTrend(),
      fetchCampaignsOverview(filter),
      fetchSubmissionsStats(filter),
      fetchBudgetOverview(),
      fetchTopCampaignsByGmv(5),
      fetchTopBrandsByGmv(5),
      fetchTopCreatorsByGmv(5),
    ]);
    setRefreshToken((t) => t + 1);
  }, [loadOwnData, filter]);

  const { refresh, isRefreshing, secondsRemaining } = useForceRefresh(
    loadData,
    'admin-dashboard',
  );

  const pendingCount = overview?.pendingSubmissions ?? 0;

  return (
    <div className="bg-[#FCFCFC] min-h-screen">
      {/* Alert banner — pending submissions */}
      {!overviewLoading && pendingCount > 0 && (
        <div className="bg-[#FFFBEB] border-b border-[#FDE68A] px-6 py-2.5 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <img
              src="/assets/alert-triangle-yellow.svg"
              alt=""
              className="w-4 h-4 shrink-0"
            />
            <span className="text-sm font-medium text-[#92400E]">
              {pendingCount} submission{pendingCount > 1 ? 's' : ''} pending
              validation
            </span>
          </div>
          <button
            onClick={() => navigate(ROUTES.ADMIN_SUBMISSIONS)}
            className="text-xs font-semibold text-[#92400E] underline underline-offset-2 hover:text-[#78350F] transition-colors"
          >
            Review now →
          </button>
        </div>
      )}

      {/* Alert banner — pending creators */}
      {pendingCreatorsCount > 0 && (
        <div className="bg-amber-50 border-b border-amber-200 px-6 py-2.5 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <span className="flex items-center justify-center w-5 h-5 rounded-full bg-amber-400 text-white text-[10px] font-bold shrink-0">
              {pendingCreatorsCount > 99 ? '99+' : pendingCreatorsCount}
            </span>
            <span className="text-sm font-medium text-amber-800">
              {pendingCreatorsCount} eligible creator
              {pendingCreatorsCount > 1 ? 's' : ''} awaiting review
            </span>
          </div>
          <button
            onClick={() => navigate(ROUTES.CREATORS_WAITLIST)}
            className="text-xs font-semibold text-amber-700 underline underline-offset-2 hover:text-amber-900 transition-colors"
          >
            Review now →
          </button>
        </div>
      )}

      {/* Alert banner — flagged submissions */}
      {flaggedCount > 0 && (
        <div className="bg-red-50 border-b border-red-200 px-6 py-2.5 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <span className="flex items-center justify-center w-5 h-5 rounded-full bg-red-500 text-white text-[10px] font-bold shrink-0">
              {flaggedCount > 99 ? '99+' : flaggedCount}
            </span>
            <span className="text-sm font-medium text-red-800">
              {flaggedCount} flagged submission
              {flaggedCount > 1 ? 's' : ''} need manual verification
            </span>
          </div>
          <button
            onClick={() => navigate(ROUTES.FLAGGED_SUBMISSIONS)}
            className="text-xs font-semibold text-red-600 underline underline-offset-2 hover:text-red-800 transition-colors"
          >
            Review now →
          </button>
        </div>
      )}

      <AdminPageHeader
        title="Campaign Dashboard"
        subtitle="Platform-wide performance — campaigns, brands & creators"
        actions={
          <RefreshButton
            onRefresh={refresh}
            isRefreshing={isRefreshing}
            secondsRemaining={secondsRemaining}
          />
        }
      />

      <div className="p-6 space-y-8">
        <section>
          <SectionLabel>Platform Overview — All Time</SectionLabel>
          <PlatformKpiGrid data={overview} loading={overviewLoading} />
        </section>

        <section>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-3">
            <p className="text-[11px] font-semibold text-[#9B919D] uppercase tracking-[0.12em] shrink-0">
              Period Activity
            </p>
            <ModernPeriodSelector
              selectedPeriod={filter}
              onPeriodChange={setFilter}
            />
          </div>
          <ActivityPeriodRow data={activity} loading={activityLoading} />
        </section>

        <section>
          <SectionLabel>Revenue &amp; ROAS</SectionLabel>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2">
              <GmvRevenueChart refreshToken={refreshToken} />
            </div>
            <RoasTrendChart refreshToken={refreshToken} />
          </div>
        </section>

        <section>
          <SectionLabel>Campaigns &amp; Submissions</SectionLabel>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:items-stretch">
            <div className="min-h-0 flex h-full lg:min-h-[360px]">
              <CampaignStatusDonut
                filter={filter}
                refreshToken={refreshToken}
              />
            </div>
            <div className="lg:col-span-2 min-h-0 flex h-full lg:min-h-[360px]">
              <SubmissionFunnelChart
                filter={filter}
                refreshToken={refreshToken}
              />
            </div>
          </div>
        </section>

        <section>
          <SectionLabel>Budget &amp; Top Campaigns</SectionLabel>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <BudgetOverviewCard refreshToken={refreshToken} />
            <div className="lg:col-span-2">
              <TopCampaignsTable refreshToken={refreshToken} />
            </div>
          </div>
        </section>

        <section>
          <SectionLabel>Top Performers</SectionLabel>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <TopBrandsTable refreshToken={refreshToken} />
            <TopCreatorsTable refreshToken={refreshToken} />
          </div>
        </section>
      </div>
    </div>
  );
};

export default AdminAnalyticsPage;
